// tslint:disable max-line-length
import { Banner } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './GetemailsSetupSlideout.module.css';
import { InstallationPageForm } from '../../../../../dashboard/pages/XRayOnboarding/InstallationPage/InstallationPage';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_SMALL } from '../../../../constants/screenSize';
import { ReactComponent as BeginCapturingLeadsSvg } from './begin-capturing-leads-in-90-seconds.svg';
import { XRaySlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export type GetemailsSetupSlideout = {
  promoterId: number;
};

const installationPageBadge = <BeginCapturingLeadsSvg />;

export const GetemailsSetupSlideout = ({
  promoterId,
}: GetemailsSetupSlideout) => {
  const hasXRaySubscriptionFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).xray;
  const chatbotId = useSelector(activeChatbotIdSelector);
  const promoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );
  const promoter = promoters.find(p => p.id === promoterId);
  const promoterCRUDActions = usePromoterCRUDActions();
  const { setRef } = useSlideoutOnClose();
  const { openUniversalUpgradeLandmineModal } = useModalActions();
  const { showSlideoutMenu } = useViewActions();

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  const { pixelName: whitelabelPixelName } = useWhitelabelConfig();
  const pixelName = whitelabelPixelName
    ? `${whitelabelPixelName} Lead Generator`
    : 'X-Ray Lead Generation Pixel';

  const promoterHasNoConditions = (promoter: PromoterModel) =>
    !promoter.conditions?.rules.length ||
    promoter.conditions.rules.every(r => r.operator === '*');

  const warnAboutMultipleRuleFreePromoters =
    promoter &&
    promoterHasNoConditions(promoter) &&
    promoters.some(p => p.id !== promoter.id && promoterHasNoConditions(p));

  const duplicateRules =
    !warnAboutMultipleRuleFreePromoters && promoter?.conditions?.rules.length
      ? promoter.conditions.rules.filter(rule =>
          promoters.some(
            p =>
              p.id !== promoter.id &&
              (p.conditions?.rules || []).some(
                otherRule =>
                  otherRule.operator === rule.operator &&
                  otherRule.value === rule.value,
              ),
          ),
        )
      : [];

  const updatePromoterStatus = async (active: boolean) => {
    await promoterCRUDActions.update(chatbotId, {
      id: promoter?.id,
      active,
    });
  };

  const customActivateAction = async () => {
    if (!hasXRaySubscriptionFlag) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['STARTBOTS-X_RAY_ACTIVATION-EDITOR'],
        feature: 'xray',
        onSuccessBilling: () => updatePromoterStatus(true),
      });
    }

    if (hasXRaySubscriptionFlag && !promoter?.active) {
      updatePromoterStatus(true);
    } else {
      updatePromoterStatus(false);
    }
  };

  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const botId = useSelector(activeChatbotIdSelector);

  const onProceedClick = () => {
    showSlideoutMenu('getEmailsEditorNewSettings', {
      promoterId: promoter?.id,
    });
  };

  useEffect(() => {
    async function fetchItems() {
      await promoterCRUDActions.retrieveAll(chatbotId, 'getemails');
    }
    fetchItems();
  }, [chatbotId, promoterCRUDActions]);

  if (!promoter) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader
        className={styles.header}
        promoter={promoter}
        title={pixelName}
        customActivateAction={customActivateAction}
        deleteWithPromoterCRUDAction
      />
      <SlideoutMenu.Content className={styles.content}>
        <Banner
          css={{
            container: styles.bannerContainer,
            header: styles.bannerHeader,
            collapsedHeader: styles.collapsedHeader,
            content: styles.bannerContent,
          }}
          withHeaderArrowIcon
          showCloseIcon={false}
          withBannerHeader
          headerText="How does X-Ray Pixel Work?"
          withLeftArea={false}
          expandedOnMount={true}
          usedInSlideout
        >
          <XRaySlideoutBanner
            textColor="white"
            alt="How does X-Ray Pixel Work?"
          />
        </Banner>

        <div className={styles.contentWrapper}>
          <InstallationPageForm
            botId={botId}
            canSkip={false}
            canCloseResults
            css={{
              diagnosticResultsContainer: styles.diagnosticResultsContainer,
            }}
            promoterId={promoterId}
            pageRemoteId={activeFacebookPage?.remoteId}
            title="Install X-Ray Pixel to Capture your Leads"
            badge={installationPageBadge}
            subtitle="Use our X-Ray Pixel to automatically capture leads from your website visitors and contact them with our automation tools. "
            thirdStepConfig={{
              title: 'Proceed to settings',
              button: 'Proceed',
              onClick: onProceedClick,
            }}
          />
          {!isMobile && (
            <div className={styles.videoExplainer}>
              <span className={styles.videoExplainerTitle}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="none"
                >
                  <path fill="#ADC6FF" d="M2.709 2.5h14.583v10.417H2.71V2.5Z" />
                  <path
                    fill="#597EF7"
                    d="M7.917 5.104v5.208l4.167-2.604-4.167-2.604Z"
                  />
                  <path
                    fill="#ADC6FF"
                    d="m4.886 16.042 1.468-.252 1.47.252a1.558 1.558 0 0 1-2.938 0ZM7.823 15l-1.469.252L4.886 15a1.558 1.558 0 0 1 2.937 0Z"
                  />
                  <path
                    fill="#597EF7"
                    d="m7.823 15-1.469.252L4.886 15H1.667v1.042h3.219l1.468-.253 1.47.253h10.51V15H7.824Z"
                  />
                  <path
                    fill="#1D39C4"
                    d="M7.917 15.52c0 .188-.031.355-.094.522H4.886a1.444 1.444 0 0 1-.094-.521c0-.188.031-.354.094-.521h2.937c.063.167.094.333.094.52Z"
                  />
                </svg>
                <span>How X-Ray Pixel will help grow your audience</span>
              </span>
              <iframe
                width="380"
                height="200"
                src="https://player.vimeo.com/video/825855525?h=e882c0cd95&badge=0&autopause=0&player_id=0&app_id=58479"
                allowFullScreen
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                title="X-Ray Pixel Email Capture System in 90 Seconds"
              />
            </div>
          )}
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
